import {
  addRoom as utilAddRoom,
  ComponentTank,
  Compressor,
  convertToStoreFormat,
  getAvailableCompressorsForRoom as getAvailableCompressors,
  getModelSpecOptions as getModelSpecDropdownOptions,
  isCompressorNumberDuplicate as checkCompressorNumberDuplicate,
  removeCompressor as utilRemoveCompressor,
  removeDryer as utilRemoveDryer,
  removeRoom as utilRemoveRoom,
  removeTank as utilRemoveTank,
  Room,
  updateCompressorSpecDetails as utilUpdateCompressorSpecDetails,
  CompressorOption,
  ComponentDryer,
} from '@/comp/Forms/SiteSurveyForm/RoomConfigurationStep/RoomConfigurationContent.utils';
import {
  Asset,
  COMPRESSOR_DATA,
  CompressorData,
  DryerType,
  ModelSpec,
  PressureTypeRoomData,
  ROOMS,
  TanksType,
  ImageData,
  IMAGES,
} from '@/store/SiteSurveyForm/constants';
import React, { Dispatch, SetStateAction } from 'react';
import { FormInstance } from 'antd';
import SiteSurveyStore from '@/store/SiteSurveyForm/SiteSurveyStore';
import { ImageUploadStore } from '@/store/ImageUpload/ImageUploadStore';
import { ImageTags } from '@/constants/CompressorImageTagData.const';

export const openCompressorDrawer = (
  roomId: number,
  setActiveRoomId: Dispatch<React.SetStateAction<number>>,
  setDrawerVisible: Dispatch<React.SetStateAction<boolean>>,
  onDrawerVisibilityChange: (isVisible: boolean) => void,
): void => {
  setActiveRoomId(roomId);
  setDrawerVisible(true);
  if (onDrawerVisibilityChange) {
    onDrawerVisibilityChange(true);
  }
};

export const closeCompressorDrawer = (
  setDrawerVisible: Dispatch<React.SetStateAction<boolean>>,
  onDrawerVisibilityChange: (isVisible: boolean) => void,
): void => {
  setDrawerVisible(false);
  if (onDrawerVisibilityChange) {
    onDrawerVisibilityChange(false);
  }
};

export const confirmCompressorSelection = (
  setDrawerVisible: Dispatch<React.SetStateAction<boolean>>,
  onDrawerVisibilityChange: (isVisible: boolean) => void,
): void => {
  closeCompressorDrawer(setDrawerVisible, onDrawerVisibilityChange);
};

const updateRooms = (
  newRooms: Room[],
  setRooms: Dispatch<SetStateAction<Room[]>>,
  form: FormInstance,
  pressureType: string,
  onChange: (values: PressureTypeRoomData) => void,
): void => {
  setRooms(newRooms);
  form.setFieldValue(pressureType, { rooms: newRooms });

  if (onChange) {
    const storeFormattedRooms = convertToStoreFormat(newRooms);
    onChange({
      rooms: storeFormattedRooms,
    });
  }
};

export const toggleCompressorSelection = (
  serialNumber: string,
  activeRoomId: number,
  activeRoom: Room,
  siteSurveyStore: SiteSurveyStore,
  rooms: Room[],
  form: FormInstance,
  pressureType: string,
  setRooms: Dispatch<SetStateAction<Room[]>>,
  onChange: (values: PressureTypeRoomData) => void,
): void => {
  if (activeRoomId === null) return;

  const currentSelectedCompressors = [...(activeRoom?.compressorsInRoom || [])];
  const isSelected = currentSelectedCompressors.includes(serialNumber);

  let newSelectedCompressors: string[];
  if (isSelected) {
    newSelectedCompressors = currentSelectedCompressors.filter(sn => sn !== serialNumber);
  } else {
    newSelectedCompressors = [...currentSelectedCompressors, serialNumber];
  }
  handleCompressorSelection(
    activeRoomId,
    newSelectedCompressors,
    rooms,
    form,
    pressureType,
    siteSurveyStore,
    setRooms,
    onChange,
  );
};

export const updateRoomCount = (
  newCount: number,
  rooms: Room[],
  setRooms: Dispatch<SetStateAction<Room[]>>,
  form: FormInstance,
  pressureType: string,
  onChange: (values: PressureTypeRoomData) => void,
): void => {
  if (newCount === rooms.length) return;

  let updatedRooms: Room[];

  if (newCount > rooms.length) {
    const newRooms = Array.from({ length: newCount - rooms.length }, (_, index) => {
      const roomId = rooms.length + index + 1;
      return {
        id: roomId,
        name: `Room ${roomId}`,
        compressorData: [],
        compressorsInRoom: [],
        tanks: [],
        dryers: [],
      };
    });
    updatedRooms = [...rooms, ...newRooms];
  } else {
    updatedRooms = rooms.slice(0, newCount);
  }
  updateRooms(updatedRooms, setRooms, form, pressureType, onChange);
};

export const getAvailableCompressorsForRoom = (
  roomId: number,
  rooms: Room[],
  siteSurveyStore: SiteSurveyStore,
): CompressorOption[] => {
  return getAvailableCompressors(rooms, roomId, siteSurveyStore.roomConfigurationStepData.assets);
};

export const getModelSpecOptions = (siteSurveyStore: SiteSurveyStore): ModelSpec[] => {
  return getModelSpecDropdownOptions(siteSurveyStore.modelSpecs);
};

export const isCompressorNumberDuplicate = (
  rooms: Room[],
  roomIndex: number,
  compressorIndex: number,
  number?: number,
): Record<number, Record<number, number>> => {
  return checkCompressorNumberDuplicate(rooms, roomIndex, compressorIndex, number);
};

export const handleAddTank = (
  roomId: number,
  pressureType: string,
  form: FormInstance,
  setRooms: Dispatch<SetStateAction<Room[]>>,
  onChange: (values: PressureTypeRoomData) => void,
) => {
  const rooms = form.getFieldValue([pressureType, ROOMS]);
  const roomIndex = getRoomIndex(rooms, roomId);
  if (roomIndex === -1) return;

  const newTankType = form.getFieldValue([pressureType, ROOMS, roomIndex, 'newTank', 'tankType']);
  const newTankValue = form.getFieldValue([pressureType, ROOMS, roomIndex, 'newTank', 'tankValue']);

  if (!newTankType || newTankValue === null || newTankValue === undefined) return;

  const newTank: ComponentTank = {
    id: `tank-${roomId}-${rooms[roomIndex].tanks?.length + 1}`,
    tankType: newTankType as TanksType,
    tankValue: newTankValue,
  };

  const currentCompressorData = form.getFieldValue([pressureType, ROOMS, roomIndex, COMPRESSOR_DATA]);
  const currentCompressorsInRoom = form.getFieldValue([pressureType, ROOMS, roomIndex, 'compressorsInRoom']);

  const updatedRooms = [...rooms];
  updatedRooms[roomIndex].tanks.push(newTank);
  updateRooms(updatedRooms, setRooms, form, pressureType, onChange);

  form.setFieldValue([pressureType, ROOMS, roomIndex, COMPRESSOR_DATA], currentCompressorData);
  form.setFieldValue([pressureType, ROOMS, roomIndex, 'compressorsInRoom'], currentCompressorsInRoom);

  form.setFieldValue([pressureType, ROOMS, roomIndex, 'newTank', 'tankType'], undefined);
  form.setFieldValue([pressureType, ROOMS, roomIndex, 'newTank', 'tankValue'], undefined);
};

export const handleRemoveTank = (
  roomId: number,
  tankId: string,
  pressureType: string,
  form: FormInstance,
  setRooms: Dispatch<SetStateAction<Room[]>>,
): void => {
  const rooms = form.getFieldValue([pressureType, ROOMS]);
  const roomIndex = getRoomIndex(rooms, roomId);
  if (roomIndex === -1) return;
  const updatedRooms = utilRemoveTank(rooms, roomId, tankId);
  form.setFieldValue([pressureType, ROOMS, roomIndex, 'tanks'], updatedRooms[roomIndex].tanks);
  setRooms(form.getFieldValue([pressureType, ROOMS]));
};

export const handleAddDryer = (
  roomId: number,
  pressureType: string,
  form: FormInstance,
  setRooms: Dispatch<SetStateAction<Room[]>>,
  onChange: (values: PressureTypeRoomData) => void,
) => {
  const rooms = form.getFieldValue([pressureType, 'rooms']);
  const roomIndex = getRoomIndex(rooms, roomId);
  if (roomIndex === -1) return;

  const newDryerType = form.getFieldValue([pressureType, 'rooms', roomIndex, 'newDryer', 'dryerType']);

  if (!newDryerType) return;

  const existingDryers = rooms[roomIndex].dryers ?? [];
  const newDryer: ComponentDryer = {
    id: `dryer-${roomId}-${existingDryers?.length + 1}`,
    dryerType: newDryerType as DryerType,
  };

  const currentCompressorData = form.getFieldValue([pressureType, 'rooms', roomIndex, 'compressorData']);
  const currentCompressorsInRoom = form.getFieldValue([pressureType, 'rooms', roomIndex, 'compressorsInRoom']);

  const updatedRooms = [...rooms];
  updatedRooms[roomIndex].dryers = [...existingDryers, newDryer];
  updateRooms(updatedRooms, setRooms, form, pressureType, onChange);

  form.setFieldValue([pressureType, 'rooms', roomIndex, 'compressorData'], currentCompressorData);
  form.setFieldValue([pressureType, 'rooms', roomIndex, 'compressorsInRoom'], currentCompressorsInRoom);

  form.setFieldValue([pressureType, 'rooms', roomIndex, 'newDryer', 'dryerType'], undefined);
};

export const handleRemoveDryer = (
  roomId: number,
  dryerId: string,
  pressureType: string,
  form: FormInstance,
  setRooms: Dispatch<SetStateAction<Room[]>>,
): void => {
  const rooms = form.getFieldValue([pressureType, 'rooms']);
  const roomIndex = getRoomIndex(rooms, roomId);
  if (roomIndex === -1) return;
  const updatedRooms = utilRemoveDryer(rooms, roomId, dryerId);
  form.setFieldValue([pressureType, 'rooms', roomIndex, 'dryers'], updatedRooms[roomIndex].dryers);
  setRooms(form.getFieldValue([pressureType, 'rooms']));
};

export const handleAddManualCompressor = (
  roomId: number,
  form: FormInstance,
  rooms: Room[],
  pressureType: string,
  setRooms: Dispatch<SetStateAction<Room[]>>,
  onChange: (values: PressureTypeRoomData) => void,
): void => {
  const roomIndex = getRoomIndex(rooms, roomId);
  if (roomIndex === -1) return;

  const manualId = `manual-${roomId}-${Date.now()}`;

  const newCompressor: Compressor = {
    id: manualId,
    serialNumber: '',
    number: undefined,
    model: '',
    modelSpec: '',
    modelSpecTechnicalName: '',
    controller: '',
    isManual: true,
    customSpecFlow: undefined,
    customSpecPower: undefined,
    customSpecPressure: undefined,
  };

  const currentRoomData = form.getFieldValue([pressureType, ROOMS, roomIndex]);
  const currentCompressorData = currentRoomData?.compressorData || [];

  form.setFieldValue([pressureType, ROOMS, roomIndex, COMPRESSOR_DATA], [...currentCompressorData, newCompressor]);

  const updatedRooms = [...rooms];
  updatedRooms[roomIndex] = {
    ...updatedRooms[roomIndex],
    compressorData: [...currentCompressorData, newCompressor],
  };
  setRooms(updatedRooms);

  form.validateFields([[pressureType, ROOMS, roomIndex, 'compressorsInRoom']]);
  if (onChange) {
    const storeFormattedRooms = convertToStoreFormat(updatedRooms);
    onChange({
      rooms: storeFormattedRooms,
    });
  }
};

export const handleRemoveCompressor = (
  roomId: number,
  compressorIndex: number,
  pressureType: string,
  form: FormInstance,
  setRooms: Dispatch<SetStateAction<Room[]>>,
  onChange: (values: PressureTypeRoomData) => void,
): void => {
  const rooms = form.getFieldValue([pressureType, ROOMS]);
  const roomIndex = getRoomIndex(rooms, roomId);
  if (roomIndex === -1) return;

  const updatedRooms = utilRemoveCompressor(rooms, roomId, compressorIndex);

  setRooms(updatedRooms);

  const updatedRoomData = updatedRooms[roomIndex];

  form.setFieldValue([pressureType, ROOMS, roomIndex, COMPRESSOR_DATA], updatedRoomData?.compressorData || []);
  form.setFieldValue([pressureType, ROOMS, roomIndex, 'compressorsInRoom'], updatedRoomData?.compressorsInRoom || []);

  if (onChange) {
    const storeFormattedRooms = convertToStoreFormat(updatedRooms);
    onChange({
      rooms: storeFormattedRooms,
    });
  }
};

export const handleCompressorSelection = (
  roomId: number,
  selectedSerialNumbers: string[],
  rooms: Room[],
  form: FormInstance,
  pressureType: string,
  siteSurveyStore: SiteSurveyStore,
  setRooms: Dispatch<SetStateAction<Room[]>>,
  onChange: (values: PressureTypeRoomData) => void,
): void => {
  const roomIndex = getRoomIndex(rooms, roomId);
  const existingCompressors = rooms[roomIndex]?.compressorData || [];

  const newSerialNumbers = selectedSerialNumbers.filter(
    serialNumber => !rooms[roomIndex]?.compressorsInRoom?.includes(serialNumber),
  );

  const formData = form.getFieldValue([pressureType, ROOMS, roomIndex, COMPRESSOR_DATA]);
  const addedCompressors = newSerialNumbers.map(serialNumber => {
    const assetInfo: Asset = siteSurveyStore.roomConfigurationStepData.assets.find(
      asset => asset.serialNumber === serialNumber,
    );
    return {
      id: `${roomId}-${serialNumber}`,
      serialNumber,
      number: undefined,
      model: assetInfo?.modelNameCpqC || assetInfo?.model || 'No data',
      modelSpec: '',
      modelSpecTechnicalName: '',
      controller: assetInfo?.localControllerTypeC || 'No data',
      isManual: false,
      customSpecFlow: undefined,
      customSpecPower: undefined,
      customSpecPressure: undefined,
    };
  });

  const remainingCompressors = existingCompressors.filter(
    compressor => compressor.isManual || selectedSerialNumbers.includes(compressor.serialNumber),
  );

  const updatedCompressorData = [...remainingCompressors, ...addedCompressors];
  let updatedFormData = [];
  formData.forEach((compressor: CompressorData) => {
    if (compressor.isManual) {
      updatedFormData.push(compressor);
    } else {
      if (selectedSerialNumbers.includes(compressor.serialNumber)) {
        updatedFormData.push(compressor);
      }
    }
  });
  updatedFormData = [...updatedFormData, ...addedCompressors];
  form.setFieldValue([pressureType, ROOMS, roomIndex, COMPRESSOR_DATA], updatedFormData);
  form.setFieldValue([pressureType, ROOMS, roomIndex, 'compressorsInRoom'], selectedSerialNumbers);

  const updatedRooms = [...rooms];
  updatedRooms[roomIndex] = {
    ...updatedRooms[roomIndex],
    compressorData: updatedCompressorData,
    compressorsInRoom: selectedSerialNumbers,
  };
  setRooms(updatedRooms);

  if (onChange) {
    const storeFormattedRooms = convertToStoreFormat(updatedRooms);
    onChange({
      rooms: storeFormattedRooms,
    });
  }
};

export const handleUpdateManualCompressorField = (
  roomId: number,
  compressorIndex: number,
  field: string,
  value: unknown,
  pressureType: string,
  form: FormInstance,
  setRooms: Dispatch<SetStateAction<Room[]>>,
  onChange: (values: PressureTypeRoomData) => void,
): void => {
  const rooms = form.getFieldValue([pressureType, ROOMS]);
  const roomIndex = getRoomIndex(rooms, roomId);

  if (roomIndex === -1) return;

  const currentRoomData = form.getFieldValue([pressureType, ROOMS, roomIndex]);
  const currentCompressorData = currentRoomData?.compressorData || [];

  const updatedCompressorData = currentCompressorData.map((compressor, index) => {
    if (index === compressorIndex) {
      return {
        ...compressor,
        [field]: value,
      };
    }
    return compressor;
  });

  form.setFieldValue([pressureType, ROOMS, roomIndex, COMPRESSOR_DATA], updatedCompressorData);

  const updatedRooms = [...rooms];
  updatedRooms[roomIndex] = {
    ...updatedRooms[roomIndex],
    compressorData: updatedCompressorData,
  };
  setRooms(updatedRooms);

  if (onChange) {
    const storeFormattedRooms = convertToStoreFormat(updatedRooms);
    onChange({
      rooms: storeFormattedRooms,
    });
  }
};

export const handleUpdateCompressorSpecDetails = (
  roomId: number,
  compressorId: string,
  field: string,
  value: number | null,
  rooms: Room[],
  pressureType: string,
  form: FormInstance,
  setRooms: Dispatch<SetStateAction<Room[]>>,
  onChange: (values: PressureTypeRoomData) => void,
): void => {
  const updatedRooms = utilUpdateCompressorSpecDetails(rooms, roomId, compressorId, field, value);
  updateRooms(updatedRooms, setRooms, form, pressureType, onChange);
};

export const handleAddRoom = (
  form: FormInstance,
  pressureType: string,
  setRooms: Dispatch<SetStateAction<Room[]>>,
  onChange: (values: PressureTypeRoomData) => void,
): void => {
  const currentRoomsFromForm = form.getFieldValue(pressureType)?.rooms || [];
  const updatedRooms = utilAddRoom(currentRoomsFromForm);
  const newCount = updatedRooms.length;

  setRooms(updatedRooms);
  form.setFieldValue(pressureType, { rooms: updatedRooms });

  if (newCount <= 5) {
    form.setFieldValue('compressorRooms', newCount);
    form.setFieldValue('otherCompressorRooms', undefined);
  } else {
    form.setFieldValue('compressorRooms', 'other');
    form.setFieldValue('otherCompressorRooms', newCount);
  }

  if (onChange) {
    const storeFormattedRooms = convertToStoreFormat(updatedRooms);
    onChange({
      rooms: storeFormattedRooms,
    });
  }
};

export const handleRemoveRoom = (
  roomId: number,
  pressureType: string,
  form: FormInstance,
  setRooms: Dispatch<SetStateAction<Room[]>>,
  onChange: (values: PressureTypeRoomData) => void,
): void => {
  const currentRoomsFromForm = form.getFieldValue(pressureType)?.rooms || [];
  if (currentRoomsFromForm.length <= 1) return;

  const updatedRooms = utilRemoveRoom(currentRoomsFromForm, roomId);
  const newCount = updatedRooms.length;

  setRooms(updatedRooms);
  form.setFieldValue(pressureType, { rooms: updatedRooms });

  if (newCount <= 5) {
    form.setFieldValue('compressorRooms', newCount);
    form.setFieldValue('otherCompressorRooms', undefined);
  } else {
    form.setFieldValue('compressorRooms', 'other');
    form.setFieldValue('otherCompressorRooms', newCount);
  }

  if (onChange) {
    const storeFormattedRooms = convertToStoreFormat(updatedRooms);
    onChange({
      rooms: storeFormattedRooms,
    });
  }
};

export const getRoomIndex = (rooms: Room[], roomId: number): number => {
  return rooms.findIndex(room => room.id === roomId);
};

export const handleAddImagesToCompressor = (
  rooms: Room[],
  imageUploadStore: ImageUploadStore,
  pressureType: string,
  form: FormInstance,
  setRooms: Dispatch<SetStateAction<Room[]>>,
  onChange: (values: PressureTypeRoomData) => void,
  setIsImageUploadModelOpen: Dispatch<SetStateAction<boolean>>,
) => {
  const { images, deviceId, reset } = imageUploadStore;

  let compressor: Compressor = null;
  let roomId: number | null = null;
  let compressorIndex: number | null = null;

  rooms.some(room =>
    room.compressorData.some((comp, j) => {
      if (comp.id === deviceId) {
        compressor = comp;
        roomId = room.id;
        compressorIndex = j;
        return true;
      }
      return false;
    }),
  );

  if (!compressor) {
    return;
  }

  const newImages = [...(compressor.images || [])];

  for (const image of images) {
    const currentMaxCount = getMaxId(image.tag, newImages);

    newImages.push({
      id: currentMaxCount + 1,
      url: image.url,
      tag: image.tag,
      originalName: image.file.name,
    });
  }

  handleUpdateManualCompressorField(roomId, compressorIndex, IMAGES, newImages, pressureType, form, setRooms, onChange);
  setIsImageUploadModelOpen(false);
  reset();
};

export const handleRemoveCompressorImage = (
  roomId: number,
  compressorIndex: number,
  imageId: number,
  tag: ImageTags,
  rooms: Room[],
  pressureType: string,
  form: FormInstance,
  setRooms: Dispatch<SetStateAction<Room[]>>,
  onChange: (values: PressureTypeRoomData) => void,
) => {
  const room = rooms.find(room => room.id === roomId);
  const images = room?.compressorData[compressorIndex].images;

  const newImages = images?.filter(image => image.id !== imageId || image.tag !== tag) || [];

  handleUpdateManualCompressorField(roomId, compressorIndex, IMAGES, newImages, pressureType, form, setRooms, onChange);
};

export const handleEditCompressorImageTag = (
  roomId: number,
  compressorIndex: number,
  imageId: number,
  newTag: ImageTags,
  oldTag: ImageTags,
  rooms: Room[],
  pressureType: string,
  form: FormInstance,
  setRooms: Dispatch<SetStateAction<Room[]>>,
  onChange: (values: PressureTypeRoomData) => void,
) => {
  const room = rooms.find(room => room.id === roomId);
  const images = room?.compressorData[compressorIndex].images;
  const imageIndex = images?.findIndex(image => image.id === imageId && image.tag === oldTag);

  if (imageIndex === -1) return;

  const newImages = [...images];
  const currentMaxCount = getMaxId(newTag, newImages);

  newImages[imageIndex] = {
    ...newImages[imageIndex],
    tag: newTag,
    id: currentMaxCount + 1,
  };

  handleUpdateManualCompressorField(roomId, compressorIndex, IMAGES, newImages, pressureType, form, setRooms, onChange);
};

const getMaxId = (tag: ImageTags, images?: ImageData[]): number => {
  const ids = images?.filter(image => image.tag === tag).map(image => image.id);

  return ids.length ? Math.max(...ids) : 0;
};
